import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { AccountManagementService } from '@core/services/account-management.service';
import { ReportService } from '@core/services/report.service';
import { ButtonDirective } from '@shared/directives/button.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { SlideInRightModalComponent } from '@shared/ui/slide-in-right-modal/slide-in-right-modal.component';
import { Subscription } from 'rxjs';

type DateMode = 'till_date' | 'custom' | null;

@Component({
  selector: 'app-mutual-funds-report',
  standalone: true,
  imports: [
    SlideInRightModalComponent,
    InputLabelComponent,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    ButtonDirective,
  ],
  templateUrl: './mutual-funds-report.component.html',
  styleUrl: './mutual-funds-report.component.scss',
})
export class MutualFundsReportComponent implements OnInit, OnDestroy {
  reportService = inject(ReportService);
  accountService = inject(AccountManagementService);
  router = inject(Router);
  subs: Subscription[] = [];
  fb = inject(FormBuilder);
  activatedRoute = inject(ActivatedRoute);
  private notify = inject(NotificationFacade);
  private change = inject(ChangeDetectorRef);

  isLoadingReport: boolean = false;
  isLoadingReportSend: boolean = false;
  isFetchingAccount = false;
  returnPathUrl: string = '';
  reportUrl!: string;
  reportTitle!: string;
  accountId!: string;
  accountNumber!: string;

  dateMode: DateMode = null;
  accountCreatedAt: string | null = null;

  dateRangeForm = this.fb.nonNullable.group({
    start_date: ['', Validators.required],
    end_date: ['', Validators.required],
  });

  get isTillDateMode(): boolean {
    return this.dateMode === 'till_date';
  }
 
  get isCustomMode(): boolean {
    return this.dateMode === 'custom';
  }
 
  get isFormReady(): boolean {
    if (this.isTillDateMode) return !!this.accountCreatedAt;
    if (this.isCustomMode) {
      const { start_date, end_date } = this.dateRangeForm.getRawValue();
      return !!start_date && !!end_date;
    }
    return false;
  }

  ngOnInit(): void {
    const sub = this.activatedRoute.queryParamMap.subscribe((params) => {
      this.accountId = params.get('accountId')!;
      this.accountNumber = params.get('accountNumber')!;
      if (this.accountId) {
        this.reportTitle = 'Mutual Funds Report';
      } else {
        this.reportTitle = 'Client Directory Report';
      }
    });
    this.subs.push(sub);
  }

  selectTillDate(): void {
    if (this.isTillDateMode) {
      // Already active → unselect
      this.dateMode = null;
      // Disable and clear custom date inputs
      this.dateRangeForm.patchValue({ start_date: '', end_date: '' });
      this.dateRangeForm.get('start_date')?.disable();
      this.dateRangeForm.get('end_date')?.disable();
    } else {
      // Select this mode
      this.dateMode = 'till_date';
      this.dateRangeForm.patchValue({ start_date: '', end_date: '' });
      this.dateRangeForm.get('start_date')?.disable();
      this.dateRangeForm.get('end_date')?.disable();
      if (!this.accountCreatedAt) {
        this.fetchAccountCreatedAt();
      }
    }
  }

  selectCustom(): void {
    if (this.isCustomMode) {
      // Already active → unselect
      this.dateMode = null;
      this.dateRangeForm.patchValue({ start_date: '', end_date: '' });
      this.dateRangeForm.get('start_date')?.disable();
      this.dateRangeForm.get('end_date')?.disable();
    } else {
      // Select this mode
      this.dateMode = 'custom';
      this.dateRangeForm.get('start_date')?.enable();
      this.dateRangeForm.get('end_date')?.enable();
    }
  }

  fetchAccountCreatedAt(): void {
    this.isFetchingAccount = true;
 
    const sub = this.accountService.getAccountDetails(this.accountNumber).subscribe({
      next: (res) => {
        this.isFetchingAccount = false;
        const account = res?.data?.[0];
        if (account?.created_at) {
          this.accountCreatedAt = account.created_at.split('T')[0];
        } else {
          this.notify.error('Could not retrieve account creation date.');
          this.rollBackMode();
        }
        this.change.detectChanges();
      },
      error: (resp) => {
        this.isFetchingAccount = false;
        this.notify.error(
          resp?.error?.message ?? 'Failed to fetch account details.'
        );
        this.rollBackMode();
        this.change.detectChanges();
      },
    });
 
    this.subs.push(sub);
  }

  rollBackMode(): void {
    this.dateMode = null;
    this.dateRangeForm.get('start_date')?.enable();
    this.dateRangeForm.get('end_date')?.enable();
  }

  getReport(type: string) {
    this.getMutualFundsReport(this.accountId, type);
  }

  getMutualFundsReport(accountId: string, type: string) {
    const isSending = type === 'report_and_email';

    // Set loading state
    isSending
      ? (this.isLoadingReportSend = true)
      : (this.isLoadingReport = true);

    const today = new Date().toISOString().split('T')[0];

    const payload = {
      start_date: this.isTillDateMode
        ? this.accountCreatedAt!
        : this.dateRangeForm.getRawValue().start_date,
      end_date: this.isTillDateMode
        ? today
        : this.dateRangeForm.getRawValue().end_date,
      type,
      account_id: accountId,
    };

    const sub = this.reportService.getMangoNairaReport(payload).subscribe({
      next: (response) => {
        isSending
          ? (this.isLoadingReportSend = false)
          : (this.isLoadingReport = false);

        this.reportUrl = response?.data?.statement;
        this.notify.success('Report generated successfully');
        this.change.detectChanges();
      },
      error: (resp) => {
        isSending
          ? (this.isLoadingReportSend = false)
          : (this.isLoadingReport = false);
        this.notify.error(resp.error.message);
        this.change.detectChanges();
      },
    });

    this.subs.push(sub);
  }

  openDocument(link: string) {
    window.open(link, '_blank');
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
